import React from 'react';
import { withRouter } from 'react-router-dom';
import { observer } from 'mobx-react';
import { reaction } from 'mobx';
import { jobPositionStore } from 'ats/pages/job-positions/JobPositionStore';
import CollapsibleSection from './components/CollapsibleSection';
import moment from 'moment';
import { Pagination } from 'ats/components/pagination/Pagination';
import FilterBar from 'ats/components/filter-bar/FilterBar';
import { ListStore } from 'shared/stores/ListStore';
import { JobApplications } from './components/JobApplications';
import UrgencyLabel from './components/UrgencyLabel';
import DrawerContainer from 'ats/components/drawer/DrawerContainer';
import { drawerStore } from 'ats/components/drawer/DrawerStore';
import JobPositionProfile from '../job-positions/JobPositionProfile';
import UserProfileAvatar from 'ats/components/user-profile-avatar/UserProfileAvatar';
import FilterDrawer from 'ats/components/filter-drawer/FilterDrawer';
import { buildAppliedFilterItems } from 'ats/components/filter-drawer/buildAppliedFilterItems';
import { jobApplicationStore } from './components/JobApplicationStore';
import { formatTimeAgo } from 'shared/utils';
import PendingTaskBadge from 'ats/components/pending-task-badge/PendingTaskBadge';
import { eventManager } from 'ats/utils/EventManager';
import { candidateSeniorityOptions } from 'components/candidates/profile/scorecards/CandidateScorecardStore';
import { candidatesStore } from 'components/candidates/CandidatesStore';
import { applicantStore } from 'ats/pages/applicants/ApplicantStore';
import {
	APPLICANT_STAGE_FILTER_GROUPS,
	JOB_APPLICATION_STAGE_FILTER_GROUPS,
	JOB_APPLICATION_STATUS_GROUP_MAP,
	getApplicantValueColor,
	getJobApplicationValueColor,
	renderApplicantStage,
	renderApplicantValueIcon,
	renderJobApplicationStage,
	renderJobApplicationValueIcon
} from 'ats/components/stage-filter-dropdown/stageFilterPresets';
import ApplicantProfile from '../applicants/ApplicantProfile';
import EmptyState from 'ats/components/empty-state/EmptyState';
import AtsTableSkeleton from 'ats/components/ats-table-skeleton/AtsTableSkeleton';
import { EMPTY_STATE_ICONS } from 'ats/components/empty-state/emptyStateIcons';
import SortInput from 'ats/components/sort-input/SortInput';
import { NON_CANDIDATE_USER_ROLES } from 'shared/security/RolesAllowed';
import {
	hasPipelineCandidateFilters,
	pickPipelineJobApplicationFilters
} from './pipelineJobApplicationFilters';
import ViewsDropdown from 'ats/components/views-dropdown/ViewsDropdown';
import { ListViewController } from 'ats/components/views-dropdown/ListViewController';
import { LIST_VIEW_SECTIONS } from 'ats/components/views-dropdown/listViewSections';

@withRouter
@observer
export class Pipeline extends React.Component {
	defaultFilterValues = {
		statusFilter: [
			{
				...jobPositionStore.getStatusByValue('open'),
				label: 'Open'
			}
		]
	};

	state = {
		searchFilter: null,
		clientFilter: [],
		lastActivityFilter: null,
		createdOnFilter: null,
		urgencyFilter: null,
		priorityFilter: null,
		statusFilter: this.defaultFilterValues.statusFilter,
		disabledFilter: null,
		ownerFilter: null,
		hiringManagerFilter: null,
		accountManagerFilter: null,
		timezoneFilter: null,
		jobIdFilter: [],
		stageFilter: null,
		internalStageFilter: [],
		seniorityFilter: [],
		englishLevelFilter: [],
		cityFilter: null,
		countryFilter: null,
		applicantFilter: null,
		candidateOwnerFilter: null,
		sourceFilter: null,
		referrerFilter: null,
		currentCompensationFilter: null,
		expectedCompensationFilter: null,
		activeJobPosition: null,
		/** For deep link / back: pass applicant id to the matching `JobApplications` */
		urlSyncJobId: null,
		urlSyncApplicantId: null,
		tagFilter: null,
		skillFilter: [],
		mineOnly: false,
		visibleColumns: jobApplicationStore.defaultTableColumnsOptions
	};

	constructor(props) {
		super(props);
		this.listStore = new ListStore(jobPositionStore, {
			entityName: 'Job Position'
		});
		this.listViewController = new ListViewController(
			LIST_VIEW_SECTIONS.PIPELINE,
			this
		);
		this.listStore.beginLoading();
	}

	syncJobApplicationFiltersToJobQuery = () => {
		const filters = pickPipelineJobApplicationFilters(
			jobApplicationStore.globalQuery
		);
		this.listStore.store.setQuery(
			'jobApplicationFilters',
			Object.keys(filters).length > 0 ? filters : null
		);
	};

	configureGlobalQueryListener = () => {
		this.globalQueryReaction = reaction(
			() => ({ ...jobApplicationStore.globalQuery }),
			() => {
				if (this.applyingListView) return;
				this.syncJobApplicationFiltersToJobQuery();
				const { limit } = this.listStore.store.query.pagination;
				this.listStore.store.setQuery('pagination', { page: 1, limit });
				this.listStore.search();
				this.listViewController.onViewStateChange();
			}
		);
	};

	async componentDidMount() {
		this.configureGlobalQueryListener();
		await this.listViewController.initialize();

		const { jobId, applicantId } = this.parsePipelinePath(
			this.props.location.pathname
		);

		if (jobId && applicantId) {
			const jobOnPage = this.isJobOnCurrentPipelinePage(jobId);
			this.setState({
				activeJobPosition: jobOnPage ? String(jobId) : null,
				urlSyncJobId: jobOnPage ? String(jobId) : null,
				urlSyncApplicantId: jobOnPage ? String(applicantId) : null
			});
			this.openApplicantProfileFromUrl(applicantId, { jobId });
		} else if (jobId && !applicantId) {
			this.setState({
				activeJobPosition: String(jobId),
				urlSyncJobId: null,
				urlSyncApplicantId: null
			});
			this.openJobPositionDrawer(String(jobId), { shouldPush: false });
		}

		eventManager.on('taskChanged', this.handleTaskChanged);
		eventManager.on(
			'jobApplicationStageChanged',
			this.handleJobApplicationStageChanged
		);
	}

	componentWillUnmount() {
		this.listViewController.cancel();
		if (this.globalQueryReaction) {
			this.globalQueryReaction();
		}

		eventManager.off('taskChanged', this.handleTaskChanged);
		eventManager.off(
			'jobApplicationStageChanged',
			this.handleJobApplicationStageChanged
		);
		jobPositionStore.resetQuery();
		jobApplicationStore.resetGlobalQuery();

		if (drawerStore.isOpen) {
			drawerStore.closeDrawer();
		}
	}

	componentDidUpdate(prevProps) {
		if (prevProps.location.pathname !== this.props.location.pathname) {
			this.handlePipelinePathnameChange();
		}
	}

	handleTaskChanged = () => {
		this.listStore.search();
	};

	handleJobApplicationStageChanged = ({ jobId }) => {
		this.listStore.search();
	};

	parsePipelinePath = pathname => {
		const p = String(pathname);

		const pathFormat = p.match(
			/\/pipeline\/jobs\/([^/]+)\/candidates\/([^/]+)\/?$/
		);
		if (pathFormat) return { jobId: pathFormat[1], applicantId: pathFormat[2] };

		const jobOnly = p.match(/\/pipeline\/jobs\/([^/]+)\/?$/);
		if (jobOnly) return { jobId: jobOnly[1], applicantId: null };

		return { jobId: null, applicantId: null };
	};

	getPipelineBasePath = () =>
		this.props.location.pathname.replace(/\/jobs(?:\/.*)?$/, '');

	getPipelinePath = (jobId, applicantId) => {
		const base = this.getPipelineBasePath();
		if (jobId == null || jobId === '') return base;
		if (applicantId != null && applicantId !== '') {
			return `${base}/jobs/${jobId}/candidates/${applicantId}`;
		}
		return `${base}/jobs/${jobId}`;
	};

	isJobOnCurrentPipelinePage = jobId =>
		this.listStore.data.some(j => String(j.id) === String(jobId));

	openApplicantProfileFromUrl = (applicantId, { jobId } = {}) => {
		const id = String(applicantId);
		const jobIdStr = jobId != null && jobId !== '' ? String(jobId) : null;

		if (
			drawerStore.isOpen &&
			drawerStore.drawerId === drawerStore.DRAWER_IDS.APPLICANT_PROFILE &&
			String(drawerStore.drawerComponentProps.applicantId) === id
		) {
			return;
		}

		drawerStore.openDrawer(
			{
				containerId: 'pipeline',
				showHeader: false,
				drawerId: drawerStore.DRAWER_IDS.APPLICANT_PROFILE,
				props: {
					applicantId: id,
					onSuccess: () => {
						this.listStore.search();
						if (jobIdStr) {
							eventManager.emit('jobApplicationsListReload', {
								jobId: jobIdStr
							});
						}
					},
					onClose: () => {
						this.setState({
							urlSyncJobId: null,
							urlSyncApplicantId: null
						});
						this.props.history.push(this.getPipelineBasePath());
						drawerStore.closeDrawer();
					}
				}
			},
			ApplicantProfile
		);
	};

	handlePipelinePathnameChange = () => {
		const p = this.props.location.pathname;

		if (!p.includes('/pipeline/jobs/')) {
			this.setState({
				activeJobPosition: null,
				urlSyncJobId: null,
				urlSyncApplicantId: null
			});
			if (drawerStore.isOpen) {
				drawerStore.closeDrawer();
			}
			return;
		}

		const { jobId, applicantId } = this.parsePipelinePath(p);
		if (!jobId) return;

		if (jobId && applicantId) {
			const jobOnPage = this.isJobOnCurrentPipelinePage(jobId);

			if (
				drawerStore.isOpen &&
				drawerStore.drawerId === drawerStore.DRAWER_IDS.JOB_POSITION_PROFILE
			) {
				drawerStore.closeDrawer();
			}

			this.setState({
				activeJobPosition: jobOnPage ? String(jobId) : null,
				urlSyncJobId: jobOnPage ? String(jobId) : null,
				urlSyncApplicantId: jobOnPage ? String(applicantId) : null
			});
			this.openApplicantProfileFromUrl(applicantId, { jobId });
			return;
		}

		this.setState({
			activeJobPosition: String(jobId),
			urlSyncJobId: null,
			urlSyncApplicantId: null
		});

		if (
			drawerStore.isOpen &&
			drawerStore.drawerId === drawerStore.DRAWER_IDS.APPLICANT_PROFILE
		) {
			drawerStore.closeDrawer();
		}
		this.openJobPositionDrawer(String(jobId), { shouldPush: false });
	};

	openJobPositionDrawer = (jobId, { shouldPush = false } = {}) => {
		const id = String(jobId);
		if (shouldPush) {
			this.props.history.push(this.getPipelinePath(id, null));
		}
		this.setState({ activeJobPosition: id });

		if (
			drawerStore.isOpen &&
			drawerStore.drawerId === drawerStore.DRAWER_IDS.JOB_POSITION_PROFILE &&
			String(drawerStore.drawerComponentProps?.jobPositionId) === id
		) {
			return;
		}

		drawerStore.openDrawer(
			{
				containerId: 'pipeline',
				showHeader: false,
				drawerId: drawerStore.DRAWER_IDS.JOB_POSITION_PROFILE,
				props: {
					jobPositionId: id,
					onSuccess: () => this.listStore.search(),
					onClose: () => {
						this.setState({ activeJobPosition: null });
						this.props.history.push(this.getPipelineBasePath());
						drawerStore.closeDrawer();
					}
				}
			},
			JobPositionProfile
		);
	};

	handlePaginationChange = value => {
		this.listStore.handlePaginationChange(
			value,
			this.listStore.store.query.pagination.limit
		);
	};

	handleResetSort = () => {
		this.listStore.handleSort('createdAt', false);
	};

	getPriority = value => {
		return jobPositionStore.priorityOptions.find(
			priority => priority.value === value
		);
	};

	getUrgency = value => {
		return jobPositionStore.urgencyOptions.find(
			urgency => urgency.value === value
		);
	};

	getStatus = value => {
		return jobPositionStore.statusOptions.find(
			status => status.value === value
		);
	};

	getDaysOpen = (createdAt, closedAt) => {
		if (!createdAt) return '-';

		const start = moment(createdAt);
		const end = closedAt ? moment(closedAt) : moment();
		const days = end.diff(start, 'days');

		return days >= 0 ? days : '-';
	};

	getDrawerFilters = () => [
		{
			entity: 'Job',
			type: 'creatable-multi',
			label: 'Job Number',
			name: 'job-number',
			onChange: newValue => {
				this.setState(
					{
						jobIdFilter: newValue || []
					},
					() => {
						drawerStore.updateDrawerProps({ filters: this.getDrawerFilters() });
					}
				);

				const ids = (newValue || [])
					.map(option => parseInt(option.value, 10))
					.filter(id => !Number.isNaN(id));

				this.listStore.store.setQuery('id', ids.length ? ids : null);
				this.handlePaginationChange(1);
			},
			value: this.state.jobIdFilter
		},
		{
			entity: 'Job',
			type: 'calendar',
			label: 'Created On',
			placeholder: 'MM/DD/YYYY',
			name: 'created-on',
			isRange: true,
			onChange: dates => {
				this.setState({ createdOnFilter: dates }, () => {
					drawerStore.updateDrawerProps({ filters: this.getDrawerFilters() });
				});

				if (dates?.[0] && dates?.[1]) {
					this.listStore.store.setQuery('createdAt', dates);
				} else {
					this.listStore.store.setQuery('createdAt', null);
				}
				this.handlePaginationChange(1);
			},
			value: this.state.createdOnFilter
		},
		{
			entity: 'Job',
			type: 'dropdown',
			label: 'Urgency',
			name: 'urgency',
			onChange: newValue => {
				this.setState(
					{
						urgencyFilter: newValue
					},
					() => {
						drawerStore.updateDrawerProps({ filters: this.getDrawerFilters() });
					}
				);
				this.listStore.store.setQuery('urgency', newValue?.value ?? null);
				this.handlePaginationChange(1);
			},
			value: this.state.urgencyFilter,
			options: [
				{ label: 'All', value: null },
				...jobPositionStore.urgencyOptions.map(urgency => ({
					...urgency,
					label: urgency.text
				}))
			]
		},
		{
			entity: 'Job',
			type: 'checkbox-group',
			label: 'Priority',
			name: 'priority',
			allowMultiple: true,
			onChange: newValue => {
				this.setState(
					{
						priorityFilter: newValue
					},
					() => {
						drawerStore.updateDrawerProps({ filters: this.getDrawerFilters() });
					}
				);
				this.listStore.store.setQuery(
					'priority',
					newValue.map(priority => priority.value)
				);
				this.handlePaginationChange(1);
			},
			value: this.state.priorityFilter,
			options: [
				...jobPositionStore.priorityOptions.map(priority => ({
					...priority,
					label: priority.text
				}))
			]
		},
		{
			entity: 'Job',
			type: 'checkbox-group',
			label: 'Status',
			name: 'status',
			allowMultiple: true,
			onChange: newValue => {
				this.setState(
					{
						statusFilter: newValue
					},
					() => {
						drawerStore.updateDrawerProps({ filters: this.getDrawerFilters() });
					}
				);
				this.listStore.store.setQuery(
					'status',
					newValue.map(status => status.value)
				);
				this.handlePaginationChange(1);
			},
			value: this.state.statusFilter,
			options: [
				...jobPositionStore.statusOptions.map(status => ({
					...status,
					label: status.text
				}))
			]
		},
		{
			entity: 'Job',
			type: 'switch',
			label: 'Published',
			id: 'disabled-switch',
			onChange: newValue => {
				this.setState({ disabledFilter: !newValue }, () => {
					drawerStore.updateDrawerProps({ filters: this.getDrawerFilters() });
				});
				this.listStore.store.setQuery('disabled', !newValue);
				this.handlePaginationChange(1);
			},
			value: this.state.disabledFilter
		},
		{
			entity: 'Job',
			type: 'user-dropdown',
			label: 'Owner',
			isMulti: true,
			role: NON_CANDIDATE_USER_ROLES,
			isClearable: true,
			onChange: newValue => {
				this.setState(
					{
						ownerFilter: newValue
					},
					() => {
						drawerStore.updateDrawerProps({ filters: this.getDrawerFilters() });
					}
				);
				this.listStore.store.setQuery(
					'owner',
					newValue.map(o => o.id)
				);
				this.handlePaginationChange(1);
			},
			value: this.state.ownerFilter
		},
		{
			entity: 'Job',
			type: 'client-contact-dropdown',
			label: 'Hiring Manager',
			isMulti: true,
			isClearable: true,
			onChange: newValue => {
				this.setState(
					{
						hiringManagerFilter: newValue
					},
					() => {
						drawerStore.updateDrawerProps({ filters: this.getDrawerFilters() });
					}
				);
				this.listStore.store.setQuery(
					'hiringManager',
					newValue.map(h => h.id)
				);
				this.handlePaginationChange(1);
			},
			value: this.state.hiringManagerFilter
		},
		{
			entity: 'Job',
			type: 'user-dropdown',
			label: 'Account Manager',
			isMulti: true,
			role: 'Account Manager',
			minSearchChars: 0,
			isClearable: true,
			onChange: newValue => {
				this.setState(
					{
						accountManagerFilter: newValue
					},
					() => {
						drawerStore.updateDrawerProps({ filters: this.getDrawerFilters() });
					}
				);
				this.listStore.store.setQuery(
					'accountManager',
					newValue.map(h => h.id)
				);
				this.handlePaginationChange(1);
			},
			value: this.state.accountManagerFilter
		},
		{
			entity: 'Job',
			type: 'timezone-dropdown',
			label: 'Time Zone',
			isMulti: true,
			isClearable: true,
			onChange: newValue => {
				this.setState(
					{
						timezoneFilter: newValue
					},
					() => {
						drawerStore.updateDrawerProps({ filters: this.getDrawerFilters() });
					}
				);
				this.listStore.store.setQuery(
					'timezones',
					newValue.map(t => t.id)
				);
				this.handlePaginationChange(1);
			},
			value: this.state.timezoneFilter
		},
		{
			entity: 'Candidates',
			type: 'applicant-dropdown',
			label: 'Name',
			isClearable: true,
			isMulti: true,
			onChange: newValue => {
				this.setState(
					{
						applicantFilter: newValue
					},
					() => {
						drawerStore.updateDrawerProps({ filters: this.getDrawerFilters() });
					}
				);
				jobApplicationStore.setGlobalQuery(
					'applicantId',
					newValue?.map(v => v.id)
				);
			},
			value: this.state.applicantFilter
		},
		{
			entity: 'Candidates',
			type: 'stage-filter-dropdown',
			label: 'Internal Stage',
			name: 'internal-stage',
			placeholder: '',
			onChange: newValue => {
				const stages = newValue || [];
				this.setState(
					{
						internalStageFilter: stages
					},
					() => {
						drawerStore.updateDrawerProps({ filters: this.getDrawerFilters() });
					}
				);

				jobApplicationStore.setGlobalQuery(
					'internalStage',
					stages.map(stage => stage.value)
				);
			},
			value: this.state.internalStageFilter,
			options: applicantStore.stageOptions,
			groups: APPLICANT_STAGE_FILTER_GROUPS,
			stageToStatus: applicantStore.stageToStatus,
			renderStage: renderApplicantStage,
			renderValueIcon: renderApplicantValueIcon,
			getValueColor: getApplicantValueColor
		},
		{
			entity: 'Candidates',
			type: 'multi-dropdown',
			label: 'Seniority',
			name: 'seniority',
			onChange: newValue => {
				this.setState(
					{
						seniorityFilter: newValue
					},
					() => {
						drawerStore.updateDrawerProps({ filters: this.getDrawerFilters() });
					}
				);
				jobApplicationStore.setGlobalQuery(
					'seniority',
					newValue?.map(v => v.value)
				);
			},
			value: this.state.seniorityFilter,
			options: [
				...candidateSeniorityOptions.map(seniority => ({
					...seniority,
					label: seniority.text
				}))
			],
			getOptionLabel: option => option.label
		},
		{
			entity: 'Candidates',
			type: 'multi-dropdown',
			label: 'English Level',
			name: 'english-level',
			onChange: newValue => {
				this.setState(
					{
						englishLevelFilter: newValue
					},
					() => {
						drawerStore.updateDrawerProps({ filters: this.getDrawerFilters() });
					}
				);
				jobApplicationStore.setGlobalQuery(
					'englishLevel',
					newValue?.map(v => v.value)
				);
			},
			value: this.state.englishLevelFilter,
			options: [
				...candidatesStore.englishLevelOptions.map(englishLevel => ({
					...englishLevel,
					label: englishLevel.text
				}))
			],
			getOptionLabel: option => option.label
		},
		{
			entity: 'Candidates',
			type: 'text',
			label: 'City',
			name: 'city',
			onKeyPress: newValue => {
				this.setState(
					{
						cityFilter: newValue
					},
					() => {
						drawerStore.updateDrawerProps({ filters: this.getDrawerFilters() });
					}
				);
			},
			onChange: newValue => {
				jobApplicationStore.setGlobalQuery('city', newValue);
			},
			value: this.state.cityFilter
		},
		{
			entity: 'Candidates',
			type: 'country-dropdown',
			label: 'Country',
			isMulti: true,
			isClearable: true,
			onChange: newValue => {
				this.setState(
					{
						countryFilter: newValue
					},
					() => {
						drawerStore.updateDrawerProps({ filters: this.getDrawerFilters() });
					}
				);
				jobApplicationStore.setGlobalQuery(
					'country',
					newValue.map(c => c.id)
				);
			},
			value: this.state.countryFilter
		},
		{
			entity: 'Candidates',
			type: 'skill-dropdown',
			label: 'Technologies',
			name: 'skills',
			onChange: newValue => {
				this.setState(
					{
						skillFilter: newValue
					},
					() => {
						drawerStore.updateDrawerProps({ filters: this.getDrawerFilters() });
					}
				);
				jobApplicationStore.setGlobalQuery(
					'skillIds',
					newValue?.map(v => v.id)
				);
			},
			value: this.state.skillFilter
		},
		{
			entity: 'Candidates',
			type: 'user-dropdown',
			label: 'Owner',
			isMulti: true,
			role: NON_CANDIDATE_USER_ROLES,
			isClearable: true,
			onChange: newValue => {
				this.setState(
					{
						candidateOwnerFilter: newValue
					},
					() => {
						drawerStore.updateDrawerProps({ filters: this.getDrawerFilters() });
					}
				);
				jobApplicationStore.setGlobalQuery(
					'owner',
					newValue.map(o => o.id)
				);
			},
			value: this.state.candidateOwnerFilter
		},
		{
			entity: 'Candidates',
			type: 'user-dropdown',
			label: 'Source',
			isMulti: true,
			role: NON_CANDIDATE_USER_ROLES,
			isClearable: true,
			onChange: newValue => {
				this.setState(
					{
						sourceFilter: newValue
					},
					() => {
						drawerStore.updateDrawerProps({ filters: this.getDrawerFilters() });
					}
				);
				jobApplicationStore.setGlobalQuery(
					'source',
					newValue.map(s => s.id)
				);
			},
			value: this.state.sourceFilter
		},
		{
			entity: 'Candidates',
			type: 'applicant-dropdown',
			label: 'Referrer',
			name: 'referrer',
			isMulti: true,
			isClearable: true,
			onChange: newValue => {
				this.setState(
					{
						referrerFilter: newValue
					},
					() => {
						drawerStore.updateDrawerProps({ filters: this.getDrawerFilters() });
					}
				);
				jobApplicationStore.setGlobalQuery(
					'referrer',
					(newValue || []).map(referrer => referrer.id)
				);
			},
			value: this.state.referrerFilter
		},
		{
			entity: 'Candidates',
			type: 'tags-dropdown',
			label: 'Tags',
			isClearable: true,
			onChange: newValue => {
				this.setState(
					{
						tagFilter: newValue
					},
					() => {
						drawerStore.updateDrawerProps({ filters: this.getDrawerFilters() });
					}
				);
				jobApplicationStore.setGlobalQuery(
					'tags',
					newValue.map(t => t.id)
				);
			},
			value: this.state.tagFilter
		},
		{
			entity: 'Candidates',
			type: 'compensation',
			label: 'Current Compensation',
			name: 'current-compensation',
			onChange: newValue => {
				this.setState(
					{
						currentCompensationFilter: newValue
					},
					() => {
						drawerStore.updateDrawerProps({
							filters: this.getDrawerFilters()
						});
					}
				);
				jobApplicationStore.setGlobalQuery('currentCompensation', newValue);
			},
			value: this.state.currentCompensationFilter
		},
		{
			entity: 'Candidates',
			type: 'compensation',
			label: 'Expected Compensation',
			name: 'expected-compensation',
			onChange: newValue => {
				this.setState(
					{
						expectedCompensationFilter: newValue
					},
					() => {
						drawerStore.updateDrawerProps({
							filters: this.getDrawerFilters()
						});
					}
				);
				jobApplicationStore.setGlobalQuery('expectedCompensation', newValue);
			},
			value: this.state.expectedCompensationFilter
		}
	];

	getBarFilters() {
		return [
			{
				type: 'search',
				label: 'Search job title or number',
				name: 'search',
				onKeyPress: newValue => {
					this.setState({
						searchFilter: newValue
					});
				},
				onChange: newValue => {
					this.listStore.store.setQuery('title', newValue);
					this.handlePaginationChange(1);
				},
				value: this.state.searchFilter
			},
			{
				type: 'client-dropdown',
				name: 'client',
				label: 'Client',
				placeholder: 'Client: All',
				isClearable: true,
				isMulti: true,
				onChange: newValue => {
					this.setState({
						clientFilter: newValue || []
					});
					this.listStore.store.setQuery(
						'clientId',
						(newValue || []).map(c => c.id)
					);
					this.handlePaginationChange(1);
				},
				value: this.state.clientFilter
			},
			{
				type: 'stage-filter-dropdown',
				label: 'Application Stage',
				name: 'stage',
				placeholder: 'Application Stage',
				onChange: newValue => {
					const stages = newValue || [];
					this.setState({ stageFilter: stages });
					jobApplicationStore.setGlobalQuery(
						'stage',
						stages.map(stage => stage.value)
					);
				},
				value: this.state.stageFilter,
				options: jobApplicationStore.stageOptions,
				groups: JOB_APPLICATION_STAGE_FILTER_GROUPS,
				stageToStatus: jobApplicationStore.stageToStatus,
				statusGroupMap: JOB_APPLICATION_STATUS_GROUP_MAP,
				renderStage: renderJobApplicationStage,
				renderValueIcon: renderJobApplicationValueIcon,
				getValueColor: getJobApplicationValueColor
			},
			{
				type: 'calendar',
				label: 'Last Activity After',
				name: 'last-activity',
				onChange: value => {
					this.setState({ lastActivityFilter: value });
					this.listStore.store.setQuery('lastActivity', value);
					this.handlePaginationChange(1);
				},
				value: this.state.lastActivityFilter
			}
		];
	}

	resetDrawerFilters = () => {
		this.setState(
			{
				searchFilter: '',
				clientFilter: [],
				lastActivityFilter: null,
				createdOnFilter: null,
				urgencyFilter: null,
				priorityFilter: null,
				statusFilter: null,
				disabledFilter: null,
				ownerFilter: null,
				hiringManagerFilter: null,
				accountManagerFilter: null,
				timezoneFilter: null,
				jobIdFilter: [],
				stageFilter: null,
				internalStageFilter: [],
				seniorityFilter: [],
				englishLevelFilter: [],
				cityFilter: '',
				countryFilter: null,
				applicantFilter: null,
				candidateOwnerFilter: null,
				sourceFilter: null,
				referrerFilter: null,
				expectedCompensationFilter: null,
				currentCompensationFilter: null,
				tagFilter: null,
				skillFilter: [],
				mineOnly: false
			},
			() => {
				drawerStore.updateDrawerProps({ filters: this.getDrawerFilters() });
			}
		);

		this.listStore.store.resetQuery();
		this.listStore.store.setQuery('sort', { column: 'createdAt', asc: false });
		jobApplicationStore.resetGlobalQuery();
	};

	getAppliedFilters = () =>
		buildAppliedFilterItems([
			...this.getBarFilters(),
			...this.getDrawerFilters()
		]);

	hasUserAppliedFilters = () => {
		const filtersWithoutStatus = [
			...this.getBarFilters(),
			...this.getDrawerFilters()
		].filter(filter => filter.name !== 'status');
		const statusValues = (this.state.statusFilter || []).map(
			status => status.value
		);
		const defaultStatusValues = this.defaultFilterValues.statusFilter.map(
			status => status.value
		);
		const hasNonDefaultStatus =
			statusValues.length > 0 &&
			(statusValues.length !== defaultStatusValues.length ||
				statusValues.some(value => !defaultStatusValues.includes(value)));

		return (
			hasNonDefaultStatus ||
			buildAppliedFilterItems(filtersWithoutStatus).length > 0
		);
	};

	renderPipelineEmptyState = () => {
		if (this.state.mineOnly) {
			return (
				<EmptyState
					size="sm"
					icon={EMPTY_STATE_ICONS.allCaughtUp}
					iconAlt="All caught up"
					message="All caught up"
					detail="You’re viewing only your pending tasks. Want to see all data?"
					actionText="Turn off Me mode"
					onActionClick={() => {
						this.setState({ mineOnly: false });
						this.listStore.store.setQuery('mineOnly', null);
						this.handlePaginationChange(1);
					}}
				/>
			);
		}

		const hasFilters = this.hasUserAppliedFilters();

		return (
			<EmptyState
				icon={hasFilters ? EMPTY_STATE_ICONS.noResults : EMPTY_STATE_ICONS.job}
				iconAlt={hasFilters ? 'No matching jobs' : 'No jobs in pipeline'}
				message={
					hasFilters
						? 'No jobs match your search criteria.'
						: 'No jobs in your pipeline yet.'
				}
				detail={
					hasFilters
						? 'Try adjusting your filters or search terms to see more results.'
						: 'Jobs will appear here once they are added to the pipeline.'
				}
				actionText={hasFilters ? 'Clear filters' : undefined}
				onActionClick={hasFilters ? this.resetDrawerFilters : undefined}
			/>
		);
	};

	openProfile = (e, job) => {
		e.stopPropagation();
		this.openJobPositionDrawer(job.id, { shouldPush: true });
	};

	render() {
		const sortOptions = [
			{
				label: 'Job Position',
				value: 'title'
			},
			{
				label: 'Client',
				value: 'client.name'
			},
			{
				label: 'Created On',
				value: 'createdAt'
			},
			{
				label: 'Priority',
				value: 'priority'
			},
			{
				label: 'Urgency',
				value: 'urgency'
			}
		];

		const isProfileOpen =
			drawerStore.isOpen &&
			[
				drawerStore.DRAWER_IDS.JOB_POSITION_PROFILE,
				drawerStore.DRAWER_IDS.APPLICANT_PROFILE,
				drawerStore.DRAWER_IDS.CLIENT_PROFILE
			].includes(drawerStore.drawerId);

		// A closing drawer must not drive the job sections, otherwise they all
		// collapse during the exit animation once `activeJobPosition` is cleared.
		const isJobPositionProfileOpen =
			drawerStore.isOpen &&
			!drawerStore.isClosing &&
			drawerStore.drawerId === drawerStore.DRAWER_IDS.JOB_POSITION_PROFILE;

		const isApplicantProfileOpenInPipeline =
			drawerStore.isOpen &&
			!drawerStore.isClosing &&
			drawerStore.drawerId === drawerStore.DRAWER_IDS.APPLICANT_PROFILE;

		const isFilterDrawerOpen =
			drawerStore.isOpen &&
			drawerStore.drawerId === drawerStore.DRAWER_IDS.FILTER_DRAWER;

		const hasActivePipelineCandidateFilters = hasPipelineCandidateFilters(
			jobApplicationStore.globalQuery
		);
		const pipelineJobsOrdered = this.listStore.data;
		const showPipelineEmptyState =
			!this.listStore.loading && pipelineJobsOrdered.length === 0;

		return (
			<div className="Pipeline">
				<div className="header-container">
					<h2>Pipeline</h2>
					<ViewsDropdown
						section={LIST_VIEW_SECTIONS.PIPELINE}
						controller={this.listViewController}
						viewStateKey={this.listViewController.getViewStateKey()}
						isModified={this.listViewController.isModified()}
					/>
					<FilterBar
						filters={this.getBarFilters()}
						appliedFilters={this.getAppliedFilters()}
						actions={
							<SortInput
								options={sortOptions}
								sort={this.listStore.store.query.sort}
								defaultSort={{ column: 'createdAt', asc: false }}
								handleSort={this.listStore.handleSort}
								onResetSort={this.handleResetSort}
								iconOnly
							/>
						}
						showMineToggle
						mineOn={this.state.mineOnly}
						onMineChange={nextValue => {
							this.setState({ mineOnly: nextValue });
							this.listStore.store.setQuery(
								'mineOnly',
								nextValue ? true : null
							);
							jobApplicationStore.setGlobalQuery(
								'mineOnly',
								nextValue ? true : null
							);
							this.handlePaginationChange(1);
						}}
						onShowMore={() => {
							if (!isFilterDrawerOpen) {
								drawerStore.openDrawer(
									{
										containerId: 'pipeline',
										drawerId: drawerStore.DRAWER_IDS.FILTER_DRAWER,
										customHeader: (
											<>
												<button
													className="filter-drawer-back-btn"
													onClick={() => drawerStore.closeDrawer()}>
													<img
														src="/ats/icons/arrow-right-purple-icon.svg"
														alt=""
													/>
												</button>
												<h3>Filters</h3>
											</>
										),
										props: {
											filters: this.getDrawerFilters(),
											getAppliedFilters: this.getAppliedFilters,
											onReset: this.resetDrawerFilters
										}
									},
									FilterDrawer
								);
							} else {
								drawerStore.closeDrawer();
							}
						}}
					/>
				</div>

				<main>
					<div
						className={`pipeline-wrapper${
							isProfileOpen ? ' is-profile-open' : ''
						}`}>
						<div
							className={`job-positions-list-wrapper${
								showPipelineEmptyState ? ' is-empty-results' : ''
							}`}>
							{showPipelineEmptyState ? (
								<div className="pipeline-jobs-empty">
									{this.renderPipelineEmptyState()}
								</div>
							) : this.listStore.loading && pipelineJobsOrdered.length === 0 ? (
								<table className="table is-fullwidth">
									<AtsTableSkeleton columns={1} rows={6} />
								</table>
							) : (
								pipelineJobsOrdered.map(job => {
									const isThis =
										String(this.state.activeJobPosition) === String(job.id);
									const isAnyPipelineContextDrawer =
										isJobPositionProfileOpen ||
										isApplicantProfileOpenInPipeline;
									const hasPendingApplicantExpand =
										!!this.state.urlSyncApplicantId &&
										this.state.urlSyncJobId != null &&
										String(this.state.urlSyncJobId) === String(job.id);

									let collapsibleOpen;
									if (isAnyPipelineContextDrawer || hasPendingApplicantExpand) {
										if (isThis) {
											collapsibleOpen = !!(
												isJobPositionProfileOpen ||
												isApplicantProfileOpenInPipeline ||
												hasPendingApplicantExpand
											);
										} else {
											collapsibleOpen = false;
										}
									} else {
										collapsibleOpen = undefined;
									}

									return (
										<div
											className={`pipeline-container ${hasActivePipelineCandidateFilters && !job.hasMatchingApplications ? 'is-dimmed' : ''} ${
												isThis &&
												(isJobPositionProfileOpen ||
													isApplicantProfileOpenInPipeline ||
													hasPendingApplicantExpand)
													? 'is-active'
													: ''
											}`}
											key={job.id}>
											<CollapsibleSection
												onClick={e => this.openProfile(e, job)}
												open={collapsibleOpen}
												title={
													<div className="is-flex gap-3 is-flex-grow-1 is-flex-shrink-1 is-flex-direction-column">
														<PendingTaskBadge
															count={job.pendingTasksCount}
															variant="count"
														/>
														<div className="is-flex is-flex-direction-row is-justify-content-space-between is-flex-grow-1 is-flex-shrink-1">
															<div className="job-position-header-title">
																<UrgencyLabel
																	mode="icon"
																	value={job.urgency}
																	text={this.getUrgency(job.urgency).text}
																/>
																<div
																	data-tooltip={`Priority: ${this.getPriority(job.priority).text}`}
																	className={`job-position-priority priority-${job.priority} tooltip is-tooltip-right`}>
																	{this.getPriority(job.priority).text}{' '}
																</div>
																<span className="client-name">
																	{job.client?.name || '--'}
																</span>
															</div>
															{isProfileOpen ? null : (
																<div className="job-position-header-info">
																	<div className="is-flex gap-1 job-position-data-info">
																		{job?.owners.length > 0 ? (
																			<>
																				<strong>Owners:</strong>

																				{job?.owners.map(({ owner }) => (
																					<UserProfileAvatar
																						imageUrl={owner?.imageUrl}
																						name={owner?.name}
																						tooltipPosition="left"
																					/>
																				))}
																			</>
																		) : (
																			<strong>No Owner</strong>
																		)}
																	</div>
																	<span className="job-position-data-info">
																		{job.lastActivity
																			? formatTimeAgo(
																					new Date(job.lastActivity)
																				)
																			: 'No Activity'}
																	</span>
																</div>
															)}
														</div>
														<div className="job-position-data-info is-flex is-justify-content-space-between is-flex-grow-1 is-flex-shrink-1">
															<div className="is-flex gap-3 is-align-items-center">
																<span>#{job.id}</span>
																<span
																	className="job-title"
																	onClick={e => this.openProfile(e, job)}>
																	{job.title}
																</span>
															</div>
															{isProfileOpen ? null : (
																<div className="is-flex gap-3">
																	<span
																		className={`status-tag status-${job.status}`}>
																		{this.getStatus(job.status).text}
																	</span>
																	<span>
																		<strong>Active candidates: </strong>
																		<span className="number-data">
																			{job.activeApplicants}
																		</span>
																	</span>
																	<span>
																		<strong>Created On: </strong>
																		{moment(job.createdAt).format('ll')}
																	</span>
																</div>
															)}
														</div>
														{isProfileOpen ? (
															<div className="job-position-data-info is-flex gap-3">
																<span
																	className={`status-tag status-${job.status}`}>
																	{this.getStatus(job.status).text}
																</span>
																<span>
																	<strong>Active candidates: </strong>
																	<span className="number-data">
																		{job.activeApplicants}
																	</span>
																</span>
																<span className="job-position-data-info">
																	{job.lastActivity
																		? formatTimeAgo(new Date(job.lastActivity))
																		: 'No Activity'}
																</span>
															</div>
														) : null}
													</div>
												}>
												<JobApplications
													job={job}
													hasMatchingApplications={job.hasMatchingApplications}
													getPipelinePath={this.getPipelinePath}
													visibleColumns={this.state.visibleColumns}
													onVisibleColumnsChange={visibleColumns =>
														this.setState({ visibleColumns })
													}
													onPipelineSelectJob={() => {
														this.setState({
															activeJobPosition: String(job.id)
														});
													}}
												/>
											</CollapsibleSection>
										</div>
									);
								})
							)}
						</div>
						<Pagination
							loading={this.listStore.loading}
							pagination={this.listStore.store.query.pagination}
							totalResults={this.listStore.store.query.pagination.totalResults}
							onPaginationChange={this.handlePaginationChange}
						/>
					</div>
					<DrawerContainer id="pipeline" />
				</main>
			</div>
		);
	}
}
